import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useApi } from '../ApiContext';
import { CONSTANTS } from '../Helpers/Configuration';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { makeStyles } from '@ellucian/react-design-system/core';
import { spacing20, spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { replacePlaceholders } from '../Helpers/replacePlaceholders';
import useApplicationForm from '../ApplicationFormLayout/useApplicationForm';
import { useSelector } from 'react-redux';
import ethosModels from '@nsr/applicant-apply-shared/src/constants/ethosModels';
import { FormattedMessage } from 'react-intl';
import { Typography, TextLink } from '@ellucian/react-design-system/core';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';

const useStyles = makeStyles({
    name: 'InquiryConfirm'
})(() => ({
    mainGrid: {
        alignItems: 'center',
    },
    message: {
        marginTop: spacing40,
        marginBottom: spacing40,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    root: {
        padding: `${spacing40} 0`,
        width: '100%',
    },
    resendPrompt: {
        marginRight: spacing20
    }
}));
const selectApplication = state => state.application;

const InquiryConfirm = ({ commType }) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const customId = 'inquiryConfirm';
    const snackbar = useSnackbar();
    const inquiry = useSelector(selectApplication);
    const { selectedLocale, template, adminTenant } = useApplicationForm();
    const api = useApi();
    if (!inquiry?.prospect) {
        return null;
    }
    const { communicationTypes, inquiryTypes } = CONSTANTS;
    const globalSettings = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const { supportedGraphqlModels } = globalSettings;

    const handleButtonClick = async () => {
        if (commType === communicationTypes.email) {
            try {
                await api.inquiries.resendInquiryEmail(inquiry.id, selectedLocale);
                snackbar.success(intl.formatMessage({ id: 'applicantResentCommunication' }, {
                    who: inquiry.prospect.email,
                }));
            } catch (_e) {
                snackbar.error(intl.formatMessage({ id: 'applicantResentCommunicationError' }));
            }
        }
    };

    const [htmlText, setHtmlText] = useState('');
    const confirmationMessageId = inquiry?.status === CONSTANTS.inquiryTypes.app ? template.settings.confirmationDescriptionId : template.settings.confirmApplicationInfoId;
    const confirmationMessage = decodeURI(intl.formatMessage({ id: confirmationMessageId }));
    useEffect(() => {
        const getText = async () => {
            const fetchedHtmlText = await replacePlaceholders(confirmationMessage, inquiry.values, template, ethosModels.inquiry.name, supportedGraphqlModels, api);
            setHtmlText(fetchedHtmlText);
        };
        getText();
    }, [[template?.settings, intl]]);

    const mainGridRef = useRef(null);
    
    useEffect(() => {
        if (mainGridRef.current) {
            const timer = setTimeout(() => {
                mainGridRef.current.setAttribute('tabindex', '-1');
                mainGridRef.current.focus({preventScroll: true});
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, []);

    return (
        <div className={classes.root}>
            <div 
                tabIndex="-1"
                ref={mainGridRef}
                className={classes.mainGrid}
                id={`${customId}_GridContainer`}
            >
                <div 
                    aria-live="polite" 
                    aria-atomic="true"
                >
                    <div id={`${customId}_message`} className={classes.message}>
                        <div>
                            <HtmlFormattedMessage htmlString={htmlText} stripHeadingTags={true} normalizeSpaces/>
                        </div>
                    </div>
                    <div id={`${customId}_retrieve`}>
                        {inquiry?.status === inquiryTypes.app &&
                            (<Typography variant="body2">
                                {commType === communicationTypes.email ?
                                    <TextLink
                                        id={`${customId}_ResendLink`}
                                        onClick={handleButtonClick}
                                    >
                                        <FormattedMessage id="resendEmailLinkTextId" />
                                    </TextLink>
                                    :
                                    <TextLink
                                        id={`${customId}_ResendLink`}
                                    >
                                        {intl.formatMessage({ id: 'inquiryConfirmResendTextLink' })}
                                    </TextLink>}
                            </Typography>)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InquiryConfirm;
